import { useEffect, useState } from 'react';
import { ApiError } from '@/lib/api/types';
import type { InternationalArrival, InternationalArrivalCriteria } from '@/types/internationalArrival';
import { fetchInternationalArrivals } from './api';

type ErrorKey = 'internationalArrivals.errorForbidden' | 'internationalArrivals.errorLoad';

/** What one completed request produced, tagged with the request it answered. */
interface Outcome {
  key: string;
  rows: InternationalArrival[];
  currPage: number;
  total: number;
  isLastPage: boolean;
  errorKey: ErrorKey | null;
}

interface InternationalArrivalsState {
  rows: InternationalArrival[];
  /** The page the server ACTUALLY returned, which is not always the one asked for. */
  currPage: number;
  total: number;
  isLastPage: boolean;
  isLoading: boolean;
  errorKey: ErrorKey | null;
}

/**
 * Runs the journal's query.
 *
 * The same shape as `useSchedule`, and for the same reasons: **loading is derived**
 * from the last outcome's tag not matching the current request rather than stored in
 * a flag an effect sets, stale answers are discarded by that tag, and a 403 is
 * reported as a refusal rather than something to retry — USER and KGA_USER are
 * refused on this read, not just on the write.
 */
export const useInternationalArrivals = (criteria: InternationalArrivalCriteria): InternationalArrivalsState => {
  // Serialized so the effect keys off the FILTER, not off the object identity.
  const key = JSON.stringify(criteria);

  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchInternationalArrivals(JSON.parse(key) as InternationalArrivalCriteria, controller.signal)
      .then((response) => {
        setOutcome({
          key,
          rows: Array.isArray(response.rows) ? response.rows : [],
          currPage: response.currPage ?? 1,
          total: response.rsCount ?? 0,
          // The server's own answer to "is there a next page", rather than a count
          // divided by a page size.
          isLastPage: response.lastBlock !== false,
          errorKey: null,
        });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setOutcome({
          key,
          rows: [],
          currPage: 1,
          total: 0,
          isLastPage: true,
          errorKey:
            caught instanceof ApiError && caught.isForbidden
              ? 'internationalArrivals.errorForbidden'
              : 'internationalArrivals.errorLoad',
        });
      });

    return () => controller.abort();
  }, [key]);


  const isCurrent = outcome?.key === key;

  return {
    // The previous page stays visible while the next loads. On an error the rows are
    // cleared: yesterday's answer under an error message is worse than none.
    rows: isCurrent || !outcome?.errorKey ? (outcome?.rows ?? []) : [],
    currPage: outcome?.currPage ?? 1,
    total: outcome?.total ?? 0,
    isLastPage: outcome?.isLastPage ?? true,
    isLoading: !isCurrent,
    errorKey: isCurrent ? outcome.errorKey : null,
  };
};
